import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React, { useRef, useState, useEffect } from 'react';
import { node, bool, string, shape, number, elementType } from 'prop-types';
import classnames from 'classnames';
import { iconUser, iconContact, iconMail, iconHome, iconWork, iconPhone, iconGlobe, iconCheck, iconWarning } from '@tesla/design-system-icons';
import { useTooltipState, Heading, TooltipWrapper, Tooltip, Icon, Text } from '@tesla/design-system-react';
import styles from './AccountTitle.module.css.js';
import FlagTitle from './FlagTitle.js';
import BackLink from '../Common/BackLink/index.js';
import useStickyHeader from '../Hooks/useStickyHeader.js';
import useIsSmallScreen from '../Hooks/useIsSmallScreen.js';

var _excluded = ["height"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var PERSON = 'PERSON';
var BUSINESS = 'BUSINESS';
var ACCOUNT_TYPE = {
  PERSON: PERSON,
  BUSINESS: BUSINESS
};
var FLAG_TITLE = {
  PRIORITY: 'Priority',
  BLOCKLISTED: 'Blocklisted'
};
var formatCamelCase = function formatCamelCase(str) {
  return str === null || str === void 0 ? void 0 : str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, function (m, chr) {
    return chr.toUpperCase();
  });
};
var defaultTranslations = {
  active: 'Active',
  inactive: 'Inactive',
  back: 'Back',
  priority: 'Customer is likely socially influential. Be mindful of interaction as it may result in media attention. Strive to provide the best experience possible, and be prepared to consult with your manager if customer has special requests.',
  blocklisted: 'Customer may be blocked from support or additional purchases. Please ask your direct manager prior to engaging with customer.'
};
var AccountTitle = function AccountTitle(_ref) {
  var eyebrowComp = _ref.eyebrowComp,
    _ref$showBack = _ref.showBack,
    showBack = _ref$showBack === void 0 ? true : _ref$showBack,
    headerText = _ref.headerText,
    accountDetails = _ref.accountDetails,
    isContact = _ref.isContact,
    t = _ref.t,
    className = _ref.className,
    customStyle = _ref.customStyle,
    asideComponent = _ref.asideComponent,
    scrollOffset = _ref.scrollOffset,
    smallScreenSize = _ref.smallScreenSize;
  var _ref2 = accountDetails || {},
    primaryEmail = _ref2.primaryEmail,
    businessAccountType = _ref2.businessAccountType,
    accountType = _ref2.accountType,
    isActive = _ref2.isActive,
    primaryPhone = _ref2.primaryPhone,
    countryCode = _ref2.countryCode,
    contactRole = _ref2.contactRole,
    _ref2$accountFlag = _ref2.accountFlag,
    _ref2$accountFlag2 = _ref2$accountFlag === void 0 ? {} : _ref2$accountFlag,
    flagType = _ref2$accountFlag2.flagType,
    displayName = _ref2.displayName;
  var isSticky = useStickyHeader({
    offset: scrollOffset
  });
  var _useIsSmallScreen = useIsSmallScreen({
      size: smallScreenSize
    }),
    _useIsSmallScreen2 = _slicedToArray(_useIsSmallScreen, 1),
    isSmallScreen = _useIsSmallScreen2[0];
  var accountTitleNode = useRef();
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    rect = _useState2[0],
    setRect = _useState2[1];
  var _useTooltipState = useTooltipState(),
    open = _useTooltipState.open,
    handlers = _useTooltipState.handlers,
    wrapperRef = _useTooltipState.wrapperRef;
  var translation = _objectSpread(_objectSpread({}, defaultTranslations), {}, {
    t: t
  });
  useEffect(function () {
    var debounce;
    var observer = new ResizeObserver(function (entries) {
      window.cancelAnimationFrame(debounce);
      debounce = window.requestAnimationFrame(function () {
        entries.forEach(function (entry) {
          var _entry$target$getBoun = entry.target.getBoundingClientRect(),
            height = _entry$target$getBoun.height,
            rest = _objectWithoutProperties(_entry$target$getBoun, _excluded);
          if ((rect === null || rect === void 0 ? void 0 : rect.height) === height) {
            return;
          }
          setRect(_objectSpread(_objectSpread({}, rest), {}, {
            height: height
          }));
        });
      });
    });
    if (accountTitleNode.current) {
      observer.observe(accountTitleNode.current);
    }
    return function () {
      window.cancelAnimationFrame(debounce);
      observer.disconnect();
    };
  });
  var Header = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("style", null, ":root {\n        --aos--components--account-details--account-info-header--block-size:\n          ".concat((rect === null || rect === void 0 ? void 0 : rect.height) || 0, "px;\n      }")), /*#__PURE__*/React.createElement("header", {
    className: classnames('tds-sticky--top', styles['tw-react--aos--components--account-info--header'], styles['tw-react--sticky-top--40px'], className, _defineProperty(_defineProperty(_defineProperty({}, styles['tw-react--header-margin'], !isSticky), styles['tw-react--header-sticky-custom-margin'], isSticky && customStyle), styles['tw-react--sticky-bg-shadow'], isSticky)),
    ref: accountTitleNode
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames('tds-flex', 'tds-flex--nowrap', {
      'tds-flex--top': isSmallScreen,
      'tds-flex--as_columns': isSmallScreen
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames('tds-flex-item', {
      'tds-flex': !isSmallScreen && isSticky,
      'tds-flex--as-rows': !isSmallScreen && isSticky
    }, styles['tw-react--header-title'])
  }, /*#__PURE__*/React.createElement(Heading, {
    is: isSticky ? 'h5' : 'h1',
    weight: "medium",
    className: classnames(_defineProperty(_defineProperty(_defineProperty({}, styles['tw-react--ad-header-font-size'], !isSticky), styles['tw-react--ad-header-margins'], isSticky), 'tds--no_padding', isSticky || showBack))
  }, headerText), eyebrowComp !== null && eyebrowComp !== void 0 ? eyebrowComp : null, /*#__PURE__*/React.createElement("div", {
    className: classnames('tds-flex', styles['tw-react--header-subtitles'])
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames('tds-flex', 'tds-flex--gutters', styles['tw-react--header-subtitles--tags'])
  }, flagType ? /*#__PURE__*/React.createElement(TooltipWrapper, _extends({}, handlers, {
    wrapperRef: wrapperRef
  }), /*#__PURE__*/React.createElement("div", {
    className: classnames(styles['tw-react--account-details-customer-flag']),
    id: "accountTitle.flagTitle"
  }, /*#__PURE__*/React.createElement(FlagTitle, {
    flagType: flagType
  })), /*#__PURE__*/React.createElement("div", {
    className: classnames(styles['tw-react--tool-tipWrapper'], styles[flagType === FLAG_TITLE.PRIORITY ? 'priority' : 'blocklisted'])
  }, /*#__PURE__*/React.createElement(Tooltip, {
    open: open,
    dense: true,
    align: "start",
    className: classnames(styles['tw-react--tool-tip'])
  }, (flagType === FLAG_TITLE.PRIORITY || flagType === FLAG_TITLE.BLOCKLISTED) && /*#__PURE__*/React.createElement("p", null, translation[formatCamelCase(flagType)])))) : null, displayName && /*#__PURE__*/React.createElement(Heading, {
    is: "h5",
    weight: "regular",
    className: classnames('tds--no_padding', 'display-flex--center', styles['tw-react--ad-header-margins'])
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    data: iconUser,
    inline: true,
    align: "text-middle"
  }), /*#__PURE__*/React.createElement(Text, {
    is: "span",
    className: styles['tw-react--ad-text-small']
  }, displayName))), isContact && contactRole && /*#__PURE__*/React.createElement(Heading, {
    is: "h5",
    weight: "regular",
    className: classnames('tds--no_padding', 'display-flex--center', styles['tw-react--ad-header-margins'])
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    data: iconContact,
    inline: true,
    align: "text-middle"
  }), /*#__PURE__*/React.createElement(Text, {
    is: "span",
    className: styles['tw-react--ad-text-small']
  }, contactRole))), accountType && (primaryEmail || businessAccountType) ? /*#__PURE__*/React.createElement(Heading, {
    is: "h5",
    weight: "regular",
    className: classnames('tds--no_padding', 'display-flex--center', styles['tw-react--ad-header-margins'])
  }, accountType === ACCOUNT_TYPE.PERSON ? /*#__PURE__*/React.createElement(Icon, {
    data: iconMail,
    inline: true,
    align: "text-middle"
  }) : /*#__PURE__*/React.createElement(Icon, {
    data: isContact ? iconHome : iconWork,
    inline: true,
    align: "text-middle"
  }), /*#__PURE__*/React.createElement(Text, {
    is: "span",
    className: styles['tw-react--ad-text-small']
  }, isContact || accountType === ACCOUNT_TYPE.PERSON ? primaryEmail : businessAccountType)) : null, primaryPhone && /*#__PURE__*/React.createElement(Heading, {
    is: "h5",
    weight: "regular",
    className: classnames('tds--no_padding', 'display-flex--center', styles['tw-react--ad-header-margins'])
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    data: iconPhone,
    inline: true,
    align: "text-middle"
  }), /*#__PURE__*/React.createElement(Text, {
    is: "span",
    className: styles['tw-react--ad-text-small']
  }, primaryPhone))), countryCode && /*#__PURE__*/React.createElement(Heading, {
    is: "h5",
    weight: "regular",
    className: classnames('tds--no_padding', 'display-flex--center', styles['tw-react--ad-header-margins'])
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    data: iconGlobe,
    inline: true,
    align: "text-middle"
  }), /*#__PURE__*/React.createElement(Text, {
    is: "span",
    className: styles['tw-react--ad-text-small']
  }, countryCode))), Object.keys(accountDetails).length !== 0 && isActive !== undefined ? /*#__PURE__*/React.createElement(Heading, {
    is: "h5",
    weight: "regular",
    className: classnames('tds--no_padding', 'display-flex--center', styles['tw-react--ad-header-margins'])
  }, isActive ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    data: iconCheck,
    inline: true,
    align: "text-middle"
  }), /*#__PURE__*/React.createElement(Text, {
    is: "span",
    className: styles['tw-react--ad-text-small']
  }, translation.active)) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    data: iconWarning,
    inline: true,
    align: "text-middle"
  }), /*#__PURE__*/React.createElement(Text, {
    is: "span",
    className: styles['tw-react--ad-text-small']
  }, translation.inactive))) : null))), /*#__PURE__*/React.createElement("div", {
    className: classnames(styles['tw-react--header-aside-container'])
  }, asideComponent))));
  var backLink = !isSticky ? /*#__PURE__*/React.createElement(BackLink, {
    t: translation,
    show: showBack
  }) : null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, backLink, Header);
};
AccountTitle.propTypes = {
  eyebrowComp: node,
  showBack: bool,
  headerText: string.isRequired,
  accountDetails: shape({
    countryCode: string.isRequired,
    accountType: string.isRequired,
    isActive: bool,
    accountName: string.isRequired,
    primaryPhone: string.isRequired,
    primaryEmail: string.isRequired
  }).isRequired,
  isContact: bool.isRequired,
  t: shape({
    active: string,
    inactive: string,
    back: string,
    priority: string,
    blocklisted: string
  }),
  className: string,
  customStyle: bool,
  scrollOffset: number,
  smallScreenSize: number,
  asideComponent: elementType
};
AccountTitle.defaultProps = {
  eyebrowComp: undefined,
  showBack: false,
  t: {},
  className: '',
  customStyle: true,
  scrollOffset: 0,
  smallScreenSize: 899,
  asideComponent: /*#__PURE__*/React.createElement(React.Fragment, null)
};

export { ACCOUNT_TYPE, BUSINESS, FLAG_TITLE, PERSON, AccountTitle as default, formatCamelCase };
